import { useEffect, useRef } from "react";
import { FilesetResolver, ImageSegmenter } from "@mediapipe/tasks-vision";

export function useVirtualBackground(
  canvasRef: React.RefObject<HTMLCanvasElement | null>,
  bgType: "none" | "blur" | "gradient" | "image",
  bgBlur: number,
  bgColors: string[],
  bgImage: string | null,
  bgLogo: string | null,
  bgLogoStyle: "none" | "watermark" | "backdrop" | "repeat",
  debouncedBgBlur: number,
  debouncedBgColors: string[],
  camera: string | undefined,
  withPermissions: boolean | undefined,
  webcamRef: React.RefObject<any>,
  headless?: boolean
) {
  const maskCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const filteredMaskCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const offscreenCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const blurCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const segmenterInputCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const imageSegmenterRef = useRef<ImageSegmenter | null>(null);
  const previousMaskDataRef = useRef<Float32Array | null>(null);
  const bgImageElementRef = useRef<HTMLImageElement | null>(null);
  const bgLogoElementRef = useRef<HTMLImageElement | null>(null);

  // Pre-load theme logo image to prevent canvas flickering inside processFrame
  useEffect(() => {
    if (!bgLogo) {
      bgLogoElementRef.current = null;
      return;
    }

    let active = true;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      if (active) {
        bgLogoElementRef.current = img;
      }
    };
    img.onerror = () => {
      console.error("Failed to load theme logo image element");
      if (active) {
        bgLogoElementRef.current = null;
      }
    };
    img.src = bgLogo;

    return () => {
      active = false;
    };
  }, [bgLogo]);

  // Pre-load the virtual background image to avoid asynchronous delays and memory leaks inside processFrame
  useEffect(() => {
    if (!bgImage) {
      bgImageElementRef.current = null;
      return () => {};
    }

    if (bgImage.startsWith("data:")) {
      const img = new Image();
      img.onload = () => {
        bgImageElementRef.current = img;
      };
      img.onerror = () => {
        console.error("Failed to load custom data URL background image");
        bgImageElementRef.current = null;
      };
      img.src = bgImage;
      return () => {};
    }

    let active = true;
    let objectUrl: string | null = null;

    // Fetch the image using a CORS fetch with cache bypass to prevent caching issues and "Canvas is not origin-clean" SecurityError.
    fetch(bgImage, { cache: "no-cache" })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        return res.blob();
      })
      .then(blob => {
        if (!active) return;
        objectUrl = URL.createObjectURL(blob);
        const img = new Image();
        img.onload = () => {
          if (active) {
            bgImageElementRef.current = img;
          }
        };
        img.onerror = () => {
          console.error("Failed to load background image from blob URL");
          if (active) {
            bgImageElementRef.current = null;
          }
        };
        img.src = objectUrl;
      })
      .catch(err => {
        console.error("Failed to pre-load background image via CORS fetch:", err);
        if (active) {
          bgImageElementRef.current = null;
        }
      });

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [bgImage]);

  // MediaPipe Tasks-Vision ImageSegmenter Setup
  useEffect(() => {
    if (bgType === "none") {
      if (imageSegmenterRef.current) {
        imageSegmenterRef.current.close();
        imageSegmenterRef.current = null;
      }
      previousMaskDataRef.current = null;
      return;
    }

    if (imageSegmenterRef.current) return;

    let active = true;

    const initSegmenter = async () => {
      try {
        const vision = await FilesetResolver.forVisionTasks(
          "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@1.0.1/wasm"
        );
        if (!active) return;

        const segmenter = await ImageSegmenter.createFromOptions(vision, {
          baseOptions: {
            modelAssetPath:
              "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite",
            delegate: "GPU",
          },
          runningMode: "VIDEO",
          outputCategoryMask: false,
          outputConfidenceMasks: true, // We want confidence masks for soft, high-quality edges!
        });

        if (!active) {
          segmenter.close();
          return;
        }

        imageSegmenterRef.current = segmenter;
      } catch (err) {
        console.error("Failed to initialize Google Tasks-Vision ImageSegmenter:", err);
      }
    };

    initSegmenter();

    return () => {
      active = false;
      if (imageSegmenterRef.current) {
        imageSegmenterRef.current.close();
        imageSegmenterRef.current = null;
      }
      previousMaskDataRef.current = null;
    };
  }, [bgType === "none"]);

  const bgBlurRef = useRef(bgBlur);
  bgBlurRef.current = bgBlur;

  const bgColorsRef = useRef(bgColors);
  bgColorsRef.current = bgColors;

  const bgLogoRef = useRef(bgLogo);
  bgLogoRef.current = bgLogo;

  const bgLogoStyleRef = useRef(bgLogoStyle);
  bgLogoStyleRef.current = bgLogoStyle;

  // Sync refs so requestAnimationFrame reads them synchronously without rebuilding effect
  useEffect(() => {
    bgBlurRef.current = bgBlur;
  }, [bgBlur]);

  useEffect(() => {
    bgColorsRef.current = bgColors;
  }, [bgColors]);

  useEffect(() => {
    bgLogoRef.current = bgLogo;
  }, [bgLogo]);

  useEffect(() => {
    bgLogoStyleRef.current = bgLogoStyle;
  }, [bgLogoStyle]);

  // Run real-time webcam frame-processing loop for segmentation
  useEffect(() => {
    if (headless) return;

    let active = true;
    let animFrameId: number;

    const processFrame = async () => {
      if (!active) return;
      const webcam = webcamRef.current;
      const segmenter = imageSegmenterRef.current;

      if (bgType !== "none" && webcam && webcam.video && webcam.video.readyState === 4 && segmenter) {
        const video = webcam.video;
        const canvas = canvasRef.current;
        if (canvas) {
          if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
          }
        }

        try {
          const timestamp = performance.now();

          // Draw the webcam video downscaled to 256x256 for extremely fast segmentation on the GPU.
          // This prevents MediaPipe from having to perform heavy downscaling of high-res (e.g. 1080x1920)
          // frames on the main thread, lowering the overhead of model execution virtually to zero.
          if (!segmenterInputCanvasRef.current) {
            segmenterInputCanvasRef.current = document.createElement("canvas");
          }
          const segmenterInputCanvas = segmenterInputCanvasRef.current;
          if (segmenterInputCanvas.width !== 256 || segmenterInputCanvas.height !== 256) {
            segmenterInputCanvas.width = 256;
            segmenterInputCanvas.height = 256;
          }
          const segmenterInputCtx = segmenterInputCanvas.getContext("2d");
          if (segmenterInputCtx) {
            segmenterInputCtx.drawImage(video, 0, 0, 256, 256);
          }

          segmenter.segmentForVideo(segmenterInputCanvas, timestamp, result => {
            if (!active) return;
            const masks = result.confidenceMasks;
            if (!masks || masks.length === 0) return;

            const mainCanvas = canvasRef.current;
            if (!mainCanvas) return;
            const ctx = mainCanvas.getContext("2d");
            if (!ctx) return;

            const width = mainCanvas.width;
            const height = mainCanvas.height;

            // 1. Get the confidence mask (index 0 is the foreground/user)
            const mask = masks[0];
            const maskData = mask.getAsFloat32Array();
            const maskWidth = mask.width;
            const maskHeight = mask.height;

            // Initialize or retrieve the persistent temporal filter mask buffer
            if (!previousMaskDataRef.current || previousMaskDataRef.current.length !== maskData.length) {
              previousMaskDataRef.current = new Float32Array(maskData);
            }
            const prevMaskData = previousMaskDataRef.current;

            // 2. Initialize or resize the intermediate mask canvas (usually 256x256)
            if (!maskCanvasRef.current) {
              maskCanvasRef.current = document.createElement("canvas");
            }
            const maskCanvas = maskCanvasRef.current;
            if (maskCanvas.width !== maskWidth || maskCanvas.height !== maskHeight) {
              maskCanvas.width = maskWidth;
              maskCanvas.height = maskHeight;
            }

            const maskCtx = maskCanvas.getContext("2d");
            if (!maskCtx) return;

            // Create alpha-mapped ImageData for the mask
            const maskImgData = maskCtx.createImageData(maskWidth, maskHeight);
            const pixels = maskImgData.data;

            // Temporal Smoothing EMA (Google Meet standard)
            // lerpAlpha of 0.35 provides high edge stability while preventing movement latency/ghosting
            const lerpAlpha = 0.35;
            const oneMinusAlpha = 1 - lerpAlpha;

            // Micro-optimized hot loop. We remove Math.max, Math.min, and Math.trunc function calls,
            // precompute division operations, and use bitwise operators for high-performance conversion to integers.
            for (let i = 0; i < maskData.length; i++) {
              const currentConfidence = maskData[i];
              const smoothedConfidence = lerpAlpha * currentConfidence + oneMinusAlpha * prevMaskData[i];
              prevMaskData[i] = smoothedConfidence;

              // (smoothedConfidence - 0.25) / 0.5 is equal to (smoothedConfidence - 0.25) * 2
              let normalized = (smoothedConfidence - 0.25) * 2;
              if (normalized < 0) {
                normalized = 0;
              } else if (normalized > 1) {
                normalized = 1;
              }

              const gatedConfidence = normalized * normalized * (3 - 2 * normalized);
              const pixelIndex = i * 4;

              pixels[pixelIndex] = 255; // R
              pixels[pixelIndex + 1] = 255; // G
              pixels[pixelIndex + 2] = 255; // B
              pixels[pixelIndex + 3] = (gatedConfidence * 255) | 0; // A (bitwise OR 0 is equivalent to Math.trunc but much faster)
            }
            maskCtx.putImageData(maskImgData, 0, 0);

            // Apply slight blur and contrast to remove jagged upscaling and erode mask halo slightly
            // at native mask resolution (e.g. 256x256) rather than high resolution (e.g., 1440x1080).
            // This reduces the filter's pixel processing load by ~23x.
            if (!filteredMaskCanvasRef.current) {
              filteredMaskCanvasRef.current = document.createElement("canvas");
            }
            const filteredMaskCanvas = filteredMaskCanvasRef.current;
            if (filteredMaskCanvas.width !== maskWidth || filteredMaskCanvas.height !== maskHeight) {
              filteredMaskCanvas.width = maskWidth;
              filteredMaskCanvas.height = maskHeight;
            }

            const filteredMaskCtx = filteredMaskCanvas.getContext("2d");
            if (!filteredMaskCtx) return;

            filteredMaskCtx.clearRect(0, 0, maskWidth, maskHeight);
            // Apply slight blur and contrast at low-res mask size. A smaller blur radius (0.8px)
            // is visually equivalent to a 3px blur on a 1440x1080 canvas when upscaled.
            filteredMaskCtx.filter = "blur(0.8px) contrast(1.5)";
            filteredMaskCtx.drawImage(maskCanvas, 0, 0);
            filteredMaskCtx.filter = "none";

            // 3. Initialize or resize the offscreen compositing canvas
            if (!offscreenCanvasRef.current) {
              offscreenCanvasRef.current = document.createElement("canvas");
            }
            const offscreenCanvas = offscreenCanvasRef.current;
            if (offscreenCanvas.width !== width || offscreenCanvas.height !== height) {
              offscreenCanvas.width = width;
              offscreenCanvas.height = height;
            }

            const offCtx = offscreenCanvas.getContext("2d");
            if (!offCtx) return;

            // Draw, upscale the already-filtered mask onto the offscreen canvas
            offCtx.save();
            offCtx.clearRect(0, 0, width, height);

            offCtx.globalCompositeOperation = "copy";
            // No filter here! Upscaling is handled instantly & smoothly by hardware interpolation (drawImage)
            offCtx.drawImage(filteredMaskCanvas, 0, 0, width, height);

            // Mask the webcam high-res frame using the smoothed mask
            offCtx.globalCompositeOperation = "source-in";
            offCtx.drawImage(video, 0, 0, width, height);
            offCtx.restore();

            // 4. Render layers onto the main visible canvas
            ctx.save();
            ctx.clearRect(0, 0, width, height);

            // Draw Virtual Background (color/gradient, photo, or blur)
            if (bgType === "blur") {
              if (!blurCanvasRef.current) {
                blurCanvasRef.current = document.createElement("canvas");
              }
              const blurCanvas = blurCanvasRef.current;
              // Downscale by 6x for extremely fast blurring (processes ~43k pixels instead of 1.5M)
              const downscaleFactor = 6;
              const downWidth = Math.trunc(width / downscaleFactor);
              const downHeight = Math.trunc(height / downscaleFactor);

              if (blurCanvas.width !== downWidth || blurCanvas.height !== downHeight) {
                blurCanvas.width = downWidth;
                blurCanvas.height = downHeight;
              }

              const blurCtx = blurCanvas.getContext("2d");
              if (blurCtx) {
                blurCtx.clearRect(0, 0, downWidth, downHeight);
                // Apply scaled-down blur radius. Bilinear/bicubic upscaling inherently adds smooth blur,
                // so scaling the radius by downscaleFactor matches the original look perfectly.
                const scaledBlur = Math.max(1, bgBlurRef.current / downscaleFactor);
                blurCtx.filter = `blur(${scaledBlur}px)`;
                blurCtx.drawImage(video, 0, 0, downWidth, downHeight);
                blurCtx.filter = "none";

                // Scale back up to fill the full canvas
                ctx.drawImage(blurCanvas, 0, 0, width, height);
              }
            } else if (bgType === "gradient") {
              // Create radial gradient centered at (50% horizontal, 35% vertical) to frame subject's head beautifully
              const cx = width / 2;
              const cy = height * 0.35;
              const grad = ctx.createRadialGradient(cx, cy, 10, cx, cy, Math.max(width, height) * 0.8);

              const currentColors = bgColorsRef.current;
              if (currentColors.length === 1) {
                grad.addColorStop(0, currentColors[0]);
                grad.addColorStop(1, currentColors[0]);
              } else {
                currentColors.forEach((color, idx) => {
                  grad.addColorStop(idx / (currentColors.length - 1), color);
                });
              }

              ctx.fillStyle = grad;
              ctx.fillRect(0, 0, width, height);
            } else if (bgType === "image" && bgImageElementRef.current) {
              const img = bgImageElementRef.current;
              const imgRatio = img.width / img.height;
              const canvasRatio = width / height;
              let dWidth = width;
              let dHeight = height;
              let dx = 0;
              let dy = 0;
              if (imgRatio > canvasRatio) {
                dWidth = height * imgRatio;
                dx = (width - dWidth) / 2;
              } else {
                dHeight = width / imgRatio;
                dy = (height - dHeight) / 2;
              }
              ctx.drawImage(img, dx, dy, dWidth, dHeight);
            }

            // Draw Step-and-Repeat tiled logo behind subject if active
            if (bgLogoRef.current && bgLogoStyleRef.current === "repeat" && bgLogoElementRef.current) {
              const el = bgLogoElementRef.current;
              ctx.save();
              ctx.globalAlpha = 0.12;
              const logoH = 35;
              const logoW = (el.width / el.height) * logoH;
              const gapX = logoW * 1.5;
              const gapY = logoH * 2.5;
              for (let y = logoH; y < height; y += gapY) {
                const isOddRow = Math.trunc(y / gapY) % 2 === 1;
                const startX = isOddRow ? gapX / 2 : 0;
                for (let x = startX; x < width + logoW; x += gapX) {
                  ctx.drawImage(el, x - logoW / 2, y - logoH / 2, logoW, logoH);
                }
              }
              ctx.restore();
            }

            // Draw Backdrop Plaque Sign behind subject if active
            if (bgLogoRef.current && bgLogoStyleRef.current === "watermark" && bgLogoElementRef.current) {
              const el = bgLogoElementRef.current;
              const h = 60;
              const w = (el.width / el.height) * h;
              const margin = 40;
              const x = margin;
              const y = margin;
              ctx.save();
              ctx.fillStyle = "rgba(255, 255, 255, 0.15)";
              ctx.strokeStyle = "rgba(255, 255, 255, 0.25)";
              ctx.lineWidth = 1;
              ctx.beginPath();
              const padX = 20;
              const padY = 12;
              if (ctx.roundRect) {
                ctx.roundRect(x - padX, y - padY, w + padX * 2, h + padY * 2, 8);
              } else {
                ctx.rect(x - padX, y - padY, w + padX * 2, h + padY * 2);
              }
              ctx.fill();
              ctx.stroke();
              ctx.globalAlpha = 0.9;
              ctx.drawImage(el, x, y, w, h);
              ctx.restore();
            }

            // Draw beautifully masked speaker on top of background
            ctx.globalCompositeOperation = "source-over";
            ctx.drawImage(offscreenCanvas, 0, 0, width, height);

            ctx.restore();
          });
        } catch (err) {
          console.error("Error running selfie segmenter:", err);
        }
      }
      animFrameId = requestAnimationFrame(processFrame);
    };

    if (bgType !== "none") {
      processFrame();
    }

    return () => {
      active = false;
      cancelAnimationFrame(animFrameId);
    };
  }, [bgType, camera, withPermissions, debouncedBgColors, bgImage, debouncedBgBlur]);

  return {
    canvasRef,
    maskCanvasRef,
    offscreenCanvasRef,
    imageSegmenterRef,
    previousMaskDataRef,
    bgImageElementRef,
    bgLogoElementRef,
  };
}
